import { faqJsonLd, offerJsonLd } from "@amtana/browser";
import type { defineBrowserModule, SeoJsonLdBlock } from "@amtana/browser";
import { pathWithLanguage } from "@amtana/i18n";
import { PRODUCT_NAME, shippedLanguage } from "@amtana/i18n/catalog";
import type { Phrase } from "@amtana/i18n/catalog";

import { familyBreadcrumb } from "./family-head.web.ts";
import {
  pricingDescription,
  pricingKeyphrase,
  pricingOffers,
  pricingQuestions,
  pricingTitle,
} from "./features/pricing/model/pricing-page.ts";
import { PRICING_WORDS_EN } from "./features/pricing/model/pricing-words-en.ts";
import { PRICING_WORDS_ES } from "./features/pricing/model/pricing-words-es.ts";
import { SITE_URL } from "./model/site-url.ts";

const PATH = "/pricing";

/** The files the page's words and numbers come from, so `lastmod` follows a price (ADR-028 point 1). */
const SOURCES = [
  "modules/site/browser/src/features/pricing/model/pricing-page.ts",
  "modules/site/browser/src/features/pricing/model/pricing-words-en.ts",
  "modules/site/browser/src/features/pricing/model/pricing-words-es.ts",
  "modules/site/browser/src/ui/sections/pricing-screen.tsx",
  "modules/site/browser/src/model/credit-words.ts",
  "modules/billing/contract/src/billing-plan-numbers.schemas.ts",
  "modules/growth/contract/src/welcome-credits.schemas.ts",
] as const;

const inLanguage = (read: (language: "en" | "es") => string): Phrase => ({
  kind: "phrase",
  read: (language) => read(shippedLanguage(language)),
});

/** One `Offer` per plan, the FAQ and the breadcrumb, in the page's language (ADR-034 point 6). */
function pricingJsonLd(language: string): readonly SeoJsonLdBlock[] {
  const shipped = shippedLanguage(language);
  const words = shipped === "es" ? PRICING_WORDS_ES : PRICING_WORDS_EN;
  const path = pathWithLanguage(PATH, shipped);
  return [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: PRODUCT_NAME,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: pricingDescription(shipped),
      offers: pricingOffers(shipped).map((offer) => offerJsonLd({ siteUrl: SITE_URL, path, ...offer })),
    },
    faqJsonLd(pricingQuestions(shipped)),
    familyBreadcrumb(shipped, [
      { name: words.breadcrumbHome, path: "/" },
      { name: words.breadcrumbPage, path: PATH },
    ]),
  ];
}

/** The pricing family's screens (ADR-034 point 1). Keep a literal `.withScreens({ ... })`, never a
 * spread: the `screen-seo` policy reads this file as text. */
export function withPricingScreens(site: ReturnType<typeof defineBrowserModule>) {
  return site.withScreens({
    pricing: {
      path: PATH,
      title: inLanguage(pricingTitle),
      seo: {
        description: inLanguage(pricingDescription),
        keyphrase: inLanguage(pricingKeyphrase),
        contentType: "landing",
        jsonLd: pricingJsonLd,
        sources: SOURCES,
      },
      load: () => import("./ui/sections/pricing-screen.tsx"),
    },
  });
}
